import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { InterviewLayout } from '../components/templates/InterviewLayout.js';
import { ChatBubble } from '../components/molecules/ChatBubble.js';
import { ChatInput } from '../components/molecules/ChatInput.js';
import { TypingIndicator } from '../components/molecules/TypingIndicator.js';
import { ErrorBanner } from '../components/molecules/ErrorBanner.js';
import { Spinner } from '../components/atoms/Spinner.js';
import { useInterview } from '../hooks/useInterview.js';

export function ChatPage() {
  const { t } = useTranslation('interview');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { messages, currentQuestion, totalQuestions, status, sendMessage, startInterview, error } =
    useInterview(token!);

  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (status === 'idle' || status === 'consent') {
      void startInterview();
    }
  }, [status, startInterview]);

  useEffect(() => {
    if (status === 'completed') {
      navigate(`/survey/${token}/complete`);
    }
  }, [status, token, navigate]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || sending) return;

    setInputValue('');
    setSending(true);
    await sendMessage(text);
    setSending(false);
  }, [inputValue, sending, sendMessage]);

  if (status === 'loading') {
    return (
      <InterviewLayout title={t('title')}>
        <div className="flex flex-1 items-center justify-center">
          <Spinner size="lg" />
        </div>
      </InterviewLayout>
    );
  }

  return (
    <InterviewLayout title={t('title')} currentStep={currentQuestion} totalSteps={totalQuestions}>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {error && <ErrorBanner message={error} className="mb-4 shrink-0" />}

        {/* Messages area - scrollable */}
        <div className="flex-1 space-y-4 overflow-y-auto pb-4">
          {messages.map((msg) => (
            <ChatBubble
              key={msg.id}
              role={msg.role}
              content={msg.content}
              timestamp={msg.timestamp}
            />
          ))}
          {sending && <TypingIndicator />}
          <div ref={bottomRef} />
        </div>

        {/* Input area - always visible at bottom */}
        <div className="shrink-0 border-t border-neutral-200 bg-white py-3">
          <ChatInput
            value={inputValue}
            onChange={setInputValue}
            onSubmit={() => void handleSubmit()}
            disabled={status !== 'chatting'}
            loading={sending}
          />
        </div>
      </div>
    </InterviewLayout>
  );
}
